<cmi-back-button />

<h2 class="text-lg font-semibold mb-2 w-full border-b border-gray-300 pb-2">
  {{ 'modelNavigation.title' | translate }}
</h2>

<!-- Navigation Path / Breadcrumb -->
<div class="mb-4 flex flex-wrap items-center gap-2 text-sm">
  <button (click)="navigateToRoot()" class="text-main hover:underline">
    {{ 'modelNavigation.root' | translate }}
  </button>
  @for (item of navigationPath(); track item.key; let i = $index; let last = $last) {
    <span class="text-gray-400">/</span>
    @if (last) {
      <span class="text-gray-700">{{ item.name }}</span>
    } @else {
      <button (click)="navigateToPathIndex(i)" class="text-main hover:underline">
        {{ item.name }}
      </button>
    }
  }
</div>
@if (error()) {
  <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
    {{ error() }}
  </div>
}
@if (fetchingData()) {
  <div class="flex items-center justify-center p-8">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-main"></div>
  </div>
} @else if (isLoading()) {
  <div class="flex items-center justify-center p-8">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-main"></div>
  </div>
} @else if (currentFields().length === 0 && currentAssocs().length === 0) {
  <div class="text-center text-gray-500 p-8">
    {{ 'modelNavigation.noData' | translate }}
  </div>
} @else {
  <div>
    @if (currentFields().length > 0) {
      <h3 class="text-sm font-semibold text-gray-700">
        {{ 'modelNavigation.fields' | translate }}
      </h3>
      <ul class="space-y-0">
        @for (field of currentFields(); track field.key) {
          <li>
            <div
              class="flex items-center justify-between py-4 hover:bg-gray-50 transition-colors"
              [title]="field.name">
              <div class="flex items-center gap-4 flex-1 min-w-0">
                <div class="shrink-0 items-center flex">
                  <cmi-provider-type-icon [providerType]="field.dataType" />
                </div>
                <div class="flex flex-col min-w-0 flex-1">
                  <span class="text-base font-medium text-gray-900 truncate">
                    {{ field.name }}
                  </span>
                </div>
              </div>
              <div class="shrink-0 flex items-center gap-2">
                @if (supportsFormatting(field)) {
                  <button
                    (click)="toggleFormattedInsert(field, $event)"
                    class="text-main group rounded shrink-0 flex items-center justify-center"
                    [title]="'contentProviders.formattedInsert' | translate">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="size-6 group-hover:fill-cmi-gray-light">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5.248 20.246H9.05m0 0h3.696m-3.696 0 5.893-16.502m0 0h-3.697m3.697 0h3.803" />
                    </svg>
                  </button>
                }
                <button
                  (click)="insertField(field)"
                  class="text-main group rounded shrink-0 flex items-center justify-center"
                  [title]="'contentProviders.add' | translate">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-6 group-hover:fill-cmi-gray-light">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                </button>
              </div>
            </div>
            @if (isFieldExpanded(field) && supportsFormatting(field)) {
              <div class="px-4 pb-4 border-t border-gray-200 bg-gray-50">
                <cmi-property-format-form
                  [property]="getFieldAsProperty(field)"
                  [insertMode]="true"
                  (applyFormat)="onApplyFormat(field, $event.formatValue)" />
              </div>
            }
          </li>
        }
      </ul>
    }
    @if (currentAssocs().length > 0) {
      <div class="border-b p-2">
        <h3 class="text-sm font-semibold text-gray-700">
          {{ 'modelNavigation.associations' | translate }}
        </h3>
      </div>
      @for (assoc of currentAssocs(); track assoc.key) {
        @for (allowedType of assoc.allowedTypes; track allowedType.key) {
          <a
            (click)="navigateToAssoc(assoc, allowedType)"
            class="flex items-center justify-between py-4 hover:bg-gray-50 group transition-colors cursor-pointer"
            [title]="assoc.key + ' -> ' + allowedType.key">
            <div class="flex items-center gap-4 flex-1 min-w-0">
              <div class="shrink-0 text-gray-500 items-center inline-flex group-hover:text-main">
                <cmi-provider-type-icon [providerType]="'Object'" />
              </div>
              <div class="flex flex-col min-w-0 flex-1">
                <span class="text-base font-medium text-gray-900 truncate group-hover:text-main">
                  {{ assoc.name }} ({{ allowedType.name }})
                </span>
              </div>
            </div>
            @if (assoc.multiplicityMax > 1) {
              <button
                (click)="insertAssoc(assoc, allowedType.pathKey); $event.stopPropagation()"
                class="text-main group rounded shrink-0 flex items-center justify-center"
                [title]="'contentProviders.add' | translate">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="size-6 group-hover:fill-cmi-gray-light">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              </button>
            }
            <div class="shrink-0">
              <svg
                class="size-6 text-gray-400 stroke-current group-hover:text-main"
                fill="none"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </a>
        }
      }
    }
  </div>
}
<ng-template #footerPortal>
  <cmi-search-bar
    class="w-full"
    [searchTerm]="searchTerm()"
    (searchTermChanged)="onSearchTermChanged($event)" />
</ng-template>
